import { useState, useRef } from "react";
import { toast } from "../../../../utils/toast";
import { post, del } from "../../../../backend/request";
import { parseS3Url, downloadS3FileSecurely } from "../../../../utils/common";
import { getUserDetails } from "../../../../backend/auth";

interface UseDocumentUploadProps {
  resourceType: "notice" | "litigation";
  resourceId?: string;
  initialDocuments?: any[];
  onDocumentDelete?: () => void;
}

export function useDocumentUpload({
  resourceType,
  resourceId,
  initialDocuments = [],
  onDocumentDelete,
}: UseDocumentUploadProps) {
  const user = getUserDetails();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [existingDocuments, setExistingDocuments] = useState<any[]>(initialDocuments);
  const [initialDocumentsCount, setInitialDocumentsCount] = useState<number>(initialDocuments.length);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const validExtensions = ["jpg", "jpeg", "png", "doc", "docx", "xls", "xlsx", "pdf"];
    const currentFileCount = existingDocuments.length + uploadedFiles.length;
    const newFileCount = files.length;
    const totalFileCount = currentFileCount + newFileCount;

    if (totalFileCount > 5) {
      toast.error(
        `You can upload a maximum of 5 files. Currently ${currentFileCount} file${
          currentFileCount !== 1 ? "s" : ""
        } uploaded.`
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    files.forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File "${file.name}" exceeds 5MB limit`);
        return;
      }

      const parts = file.name.split(".");
      const extension = parts.length > 0 ? parts[parts.length - 1].toLowerCase() : "";

      if (!extension || !validExtensions.includes(extension)) {
        invalidFiles.push(file.name);
        return;
      }

      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      toast.error(
        `Invalid file type. Supported formats: jpg, jpeg, png, doc, docx, xls, xlsx, pdf`
      );
    }

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
      setIsUploading(true);

      try {
        const uploadPromises = validFiles.map(async (file) => {
          const uploadFormData = new FormData();
          uploadFormData.append("file", file);
          uploadFormData.append("resource_type", resourceType);
          uploadFormData.append("file_type", `${resourceType}/document`);
          uploadFormData.append("resource_field", `${resourceType}_document`);
          uploadFormData.append("skip_deep_scan", "true");

          const response = await post(
            "/api/v1/upload",
            uploadFormData,
            { "Content-Type": "multipart/form-data" },
            true
          );

          const responseData = response && response.data;
          if (responseData && responseData.url) {
            const fileInfo = responseData.file_info || {};
            const uploadedUrl = responseData.url;

            let s3Key = "";
            let s3Bucket = "";
            let s3Region = "";

            const s3Params = parseS3Url(uploadedUrl);
            if (s3Params) {
              s3Bucket = s3Params.s3_bucket;
              s3Region = s3Params.s3_region;
              s3Key = s3Params.s3_key;
            }

            return {
              asset_type: `${resourceType}_document`,
              name: fileInfo.filename || file.name,
              s3_key: s3Key,
              s3_bucket: s3Bucket,
              s3_region: s3Region,
              file_path: uploadedUrl,
              content_type: fileInfo.content_type || file.type,
              file_size: fileInfo.size || file.size,
              status: "active",
              uploaded_by_id: user.user_id,
              url: uploadedUrl,
              file_info: fileInfo,
            };
          }
          return null;
        });

        const newDocuments = await Promise.all(uploadPromises);
        const validDocuments = newDocuments.filter((doc) => doc !== null);
        setUploadedDocuments((prev) => [...prev, ...validDocuments]);
      } catch (error: any) {
        const errorMessage = (error && error.response && error.response.data && error.response.data.message) ||
                             (error && error.message) ||
                             "Failed to upload files";
        toast.error(errorMessage);
      } finally {
        setIsUploading(false);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadedDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingDocument = (document: any) => {
    setDocumentToDelete(document);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteDocument = async () => {
    if (!resourceId || !documentToDelete || !documentToDelete.id) {
      toast.error("Unable to delete document");
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
      return;
    }

    try {
      await del(
        `/api/v1/${resourceType}_management/${resourceType}s/${resourceId}/assets/${documentToDelete.id}`
      );
      setExistingDocuments((prev) => prev.filter((doc) => doc.id !== documentToDelete.id));
      toast.success("Document deleted successfully");
      if (onDocumentDelete) {
        onDocumentDelete();
      }
    } catch (error: any) {
      const errorMessage = (error && error.response && error.response.data && error.response.data.message) ||
                           (error && error.message) ||
                           "Failed to delete document";
      toast.error(errorMessage);
    } finally {
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
    }
  };

  const cancelDeleteDocument = () => {
    setShowDeleteConfirm(false);
    setDocumentToDelete(null);
  };

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDownloadNew = (file: File) => {
    try {
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Silently handle URL revocation errors
        }
      }, 1000);
    } catch (err: any) {
      const errorMessage = (err && err.response && err.response.data && err.response.data.message) ||
                           (err && err.message) ||
                           "Unable to download file";
      toast.error(errorMessage);
    }
  };

  const handleDownloadExisting = (document: any) => {
    if (document.file_path) {
      downloadS3FileSecurely(document.name || "document", document.file_path);
    } else {
      toast.error("Unable to download document");
    }
  };

  const resetDocuments = () => {
    setExistingDocuments([]);
    setInitialDocumentsCount(0);
    setUploadedFiles([]);
    setUploadedDocuments([]);
    setShowDeleteConfirm(false);
    setDocumentToDelete(null);
  };

  const loadDocuments = (documents: any[]) => {
    setExistingDocuments(documents || []);
    setInitialDocumentsCount(documents ? documents.length : 0);
  };

  const hasDocumentsChanged = () => {
    return uploadedDocuments.length > 0 || existingDocuments.length !== initialDocumentsCount;
  };

  return {
    fileInputRef,
    existingDocuments,
    uploadedFiles,
    uploadedDocuments,
    isUploading,
    showDeleteConfirm,
    documentToDelete,
    handleFileSelect,
    handleRemoveFile,
    handleDeleteExistingDocument,
    confirmDeleteDocument,
    cancelDeleteDocument,
    handleChooseFile,
    handleDownloadNew,
    handleDownloadExisting,
    resetDocuments,
    loadDocuments,
    hasDocumentsChanged,
  };
}